import { Collapse, Row, Col, Button, Segmented } from "antd";
import { PlusOutlined, MinusOutlined, CloseOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeadset } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { BusinessState } from "redux-context/business";
import { getSupportChat } from "services/SauceService";
import { useState, useEffect } from "react";
import { FAQ_ACCESSED_KEY } from "constants/localStorageKeys";
import "./FAQ.scss";

const { Panel } = Collapse;

interface ExpandIconProps {
  isActive: boolean;
}

const ExpandIcon: React.FC<ExpandIconProps> = ({ isActive }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        backgroundColor: isActive
          ? "#f6a623"
          : isHovered
          ? "#f6a623"
          : "#e5e7eb",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color 0.3s ease",
        cursor: "pointer",
        position: "relative",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          position: "relative",
          width: 14,
          height: 14,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <PlusOutlined
          style={{
            fontSize: 14,
            color: isHovered && !isActive ? "#ffffff" : "#1f1f1f",
            position: "absolute",
            opacity: isActive ? 0 : 1,
            transform:
              isHovered && !isActive ? "rotate(90deg)" : "rotate(0deg)",
            transition:
              "opacity 0.3s ease, transform 0.3s ease, color 0.3s ease",
          }}
        />
        <MinusOutlined
          style={{
            fontSize: 14,
            color: "#ffffff",
            position: "absolute",
            opacity: isActive && !isHovered ? 1 : 0,
            transform:
              isActive && !isHovered
                ? "rotate(0deg)"
                : isActive && isHovered
                ? "rotate(45deg)"
                : "rotate(0deg)",
            transition:
              "opacity 0.3s ease, transform 0.3s ease, color 0.3s ease",
          }}
        />
        <CloseOutlined
          style={{
            fontSize: 14,
            color: "#ffffff",
            position: "absolute",
            opacity: isActive && isHovered ? 1 : 0,
            transform:
              isActive && isHovered
                ? "rotate(0deg)"
                : isActive
                ? "rotate(-45deg)"
                : "rotate(0deg)",
            transition:
              "opacity 0.3s ease, transform 0.3s ease, color 0.3s ease",
          }}
        />
      </div>
    </div>
  );
};

interface FAQItem {
  key: string;
  question: string;
  answer: string;
}

interface FAQTab {
  key: string;
  label: string;
  videoUrl: string;
  videoDescription: string;
}

const faqTabs: FAQTab[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    videoUrl:
      "https://demo.arcade.software/n9AaOySZpLWTHvTDgyRx?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true",
    videoDescription:
      "Watch this quick overview to understand how the Dashboard works and how to track your overall performance at a glance.",
  },
  {
    key: "collabs",
    label: "Collabs",
    videoUrl:
      "https://demo.arcade.software/n9AaOySZpLWTHvTDgyRx?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true",
    videoDescription:
      "Learn how to create and manage individual collaborations with creators.",
  },
  {
    key: "campaigns",
    label: "Campaigns",
    videoUrl:
      "https://demo.arcade.software/n9AaOySZpLWTHvTDgyRx?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true",
    videoDescription:
      "Discover how to create and manage campaigns that reach multiple creators at once.",
  },
  {
    key: "applications",
    label: "Applications",
    videoUrl:
      "https://demo.arcade.software/n9AaOySZpLWTHvTDgyRx?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true",
    videoDescription:
      "Learn how to review and manage creator applications for your campaigns and collabs.",
  },
  {
    key: "creators",
    label: "Creators",
    videoUrl:
      "https://demo.arcade.software/n9AaOySZpLWTHvTDgyRx?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true",
    videoDescription:
      "Learn how to find, filter, and work with creators on the Mustard platform.",
  },
  {
    key: "reports",
    label: "Reports",
    videoUrl:
      "https://demo.arcade.software/n9AaOySZpLWTHvTDgyRx?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true",
    videoDescription:
      "Understand how to access and analyze reports for your campaigns and collaborations.",
  },
  {
    key: "messenger",
    label: "Messenger",
    videoUrl:
      "https://demo.arcade.software/n9AaOySZpLWTHvTDgyRx?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true",
    videoDescription:
      "Learn how to communicate with creators through the Messenger feature.",
  },
  {
    key: "settings",
    label: "Settings",
    videoUrl:
      "https://demo.arcade.software/n9AaOySZpLWTHvTDgyRx?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true",
    videoDescription:
      "Configure your account settings, preferences, and business information.",
  },
];

const faqQuestions: Record<string, FAQItem[]> = {
  dashboard: [
    {
      key: "d1",
      question: "How do I customize my dashboard widgets?",
      answer:
        "You can customize your dashboard by clicking the settings icon in the top right corner. From there, you can drag and drop widgets, resize them, and rearrange the layout to match your preferences. Changes are saved automatically.",
    },
    {
      key: "d2",
      question: "What do the different slot types mean?",
      answer:
        "Slot types categorize your collaboration opportunities. Basic slots are standard collaborations, while Premium slots offer enhanced features like priority placement and extended creator access. You can manage your slot allocation in the Settings section.",
    },
    {
      key: "d3",
      question: "How do I view my performance metrics?",
      answer:
        "Your performance metrics are displayed in real-time on the dashboard. You can view engagement rates, reach, impressions, and conversion data for all your active collaborations. Click on any metric card to see detailed breakdowns.",
    },
  ],
  collabs: [
    {
      key: "c1",
      question: "How do I create a new collab?",
      answer:
        "To create a new collab, navigate to the Collabs section and click the 'Create Collab' button. You'll be able to select a creator, set collaboration details, compensation, and timeline. Once submitted, the creator will receive an invitation.",
    },
    {
      key: "c2",
      question: "Can I edit a collab after it's been created?",
      answer:
        "Yes, you can edit collab details before the creator accepts. Once accepted, you can still modify certain aspects like visit time and compensation through the Messenger. Major changes require creator approval.",
    },
    {
      key: "c3",
      question: "What happens if a creator declines my collab invite?",
      answer:
        "If a creator declines, you'll receive a notification. The collab will be marked as declined, and you can choose to modify the offer and resend, or create a new collab with different creators. Your slot will remain available.",
    },
    {
      key: "c4",
      question: "How do I track collab completion?",
      answer:
        "You can track collab status in the Collabs section. Once a creator completes their visit and posts content, you'll see updates in real-time. You can also set up notifications to be alerted when content goes live.",
    },
  ],
  campaigns: [
    {
      key: "cp1",
      question: "How do I create a campaign?",
      answer:
        "Go to the Campaigns section and click 'Create Campaign'. Set your target criteria, budget, timeline, and campaign goals. You can filter by platform, follower count, location, and more. Once published, eligible creators will be able to apply.",
    },
    {
      key: "cp2",
      question: "What's the difference between a Collab and a Campaign?",
      answer:
        "A Collab is a single collaboration with one specific creator. A Campaign allows you to invite multiple creators at once based on filters and criteria. Campaigns are ideal for reaching a broader audience quickly with a unified message.",
    },
    {
      key: "cp3",
      question: "How does compensation work for campaigns?",
      answer:
        "You set compensation at the campaign level, and it applies to all accepted creators. You can offer Experience Credit, cash, or a combination. Compensation is processed automatically when creators complete their visits.",
    },
    {
      key: "cp4",
      question: "Can I pause or cancel a campaign?",
      answer:
        "Yes, you can pause active campaigns at any time. Paused campaigns won't accept new applications but existing accepted creators remain active. You can also cancel campaigns, which will notify all involved creators.",
    },
  ],
  applications: [
    {
      key: "a1",
      question: "How do I review applications?",
      answer:
        "When creators apply to your campaigns or collabs, you'll receive notifications. Go to the Applications section to see all pending applications. You can view creator profiles, engagement rates, and previous work before accepting or rejecting.",
    },
    {
      key: "a2",
      question: "What information should I consider when reviewing?",
      answer:
        "Consider the creator's engagement rate, follower count, content quality, audience demographics, and how well they align with your brand. Review their portfolio and past collaborations to ensure they're a good fit.",
    },
    {
      key: "a3",
      question: "How long do I have to respond to an application?",
      answer:
        "There's no strict deadline, but we recommend responding within 48 hours to maintain good relationships with creators. Some campaigns may have application deadlines specified in the campaign details.",
    },
    {
      key: "a4",
      question: "Can I save applications for later review?",
      answer:
        "Yes, applications remain in your Applications section until you take action. You can filter and sort them by date, creator metrics, or campaign to help prioritize your review process.",
    },
  ],
  creators: [
    {
      key: "cr1",
      question: "How do I search for creators?",
      answer:
        "Use the search bar and filters on the Creators page. You can filter by platform (Instagram/TikTok), minimum followers, engagement rate, categories, location, gender, and audience demographics. Combine multiple filters to find perfect matches.",
    },
    {
      key: "cr2",
      question: "Can I save favorite creators?",
      answer:
        "Yes! Click the heart icon on any creator's profile to save them to your favorites. Favorite creators are accessible in the 'Saved' tab on the Creators page for quick access when creating collabs or campaigns.",
    },
    {
      key: "cr3",
      question: "How do I contact a creator?",
      answer:
        "Once a creator is booked or you have an active collaboration, you can message them directly through the Messenger feature. This allows you to coordinate visit details, discuss content requirements, and maintain ongoing communication.",
    },
    {
      key: "cr4",
      question: "What metrics should I look for in a creator?",
      answer:
        "Key metrics include engagement rate (aim for 3%+), follower authenticity, audience demographics matching your target market, content quality, and posting consistency. The platform provides detailed analytics for each creator.",
    },
  ],
  reports: [
    {
      key: "r1",
      question: "How do I generate a report?",
      answer:
        "Go to the Reports section and select the campaign or collab you want to analyze. Click 'Generate Report' to create a detailed analytics report including views, engagement, reach, and conversion metrics. Reports can be exported as PDF or CSV.",
    },
    {
      key: "r2",
      question: "What data is included in reports?",
      answer:
        "Reports include total impressions, reach, engagement rate, likes, comments, shares, click-through rates, and estimated conversions. You can also see demographic breakdowns of the audience reached and content performance over time.",
    },
    {
      key: "r3",
      question: "How often are reports updated?",
      answer:
        "Reports are updated in real-time as creators publish content and engagement data flows in. You can refresh reports at any time to see the latest metrics. Historical data is available for all completed collaborations.",
    },
    {
      key: "r4",
      question: "Can I compare multiple campaigns in one report?",
      answer:
        "Yes, you can create comparative reports by selecting multiple campaigns or collabs. This allows you to analyze performance across different time periods, creators, or campaign types to identify trends and optimize your strategy.",
    },
  ],
  messenger: [
    {
      key: "m1",
      question: "How do I start a conversation with a creator?",
      answer:
        "Once you have an active collaboration or have accepted an application, you can access the Messenger from the Collabs or Applications section. Click on a creator's name to open the conversation thread where you can coordinate details.",
    },
    {
      key: "m2",
      question: "Can I send files or documents through Messenger?",
      answer:
        "Yes, you can send images, documents, and links through Messenger. This is useful for sharing brand guidelines, campaign briefs, or reference materials. Files are securely stored and accessible throughout the collaboration.",
    },
    {
      key: "m3",
      question: "How do I manage multiple conversations?",
      answer:
        "The Messenger section shows all your active conversations in a list. You can search, filter, and sort conversations. Unread messages are highlighted, and you'll receive notifications when creators respond to your messages.",
    },
    {
      key: "m4",
      question: "Can I schedule messages or set reminders?",
      answer:
        "While direct scheduling isn't available, you can use the Messenger to coordinate visit times and set up reminders. The system will also send automatic notifications for important collaboration milestones like visit confirmations.",
    },
  ],
  settings: [
    {
      key: "s1",
      question: "How do I update my business information?",
      answer:
        "Go to Settings and select 'Business Info'. You can update your business name, description, logo, contact information, and location details. Changes are saved immediately and will be visible to creators when they view your profile.",
    },
    {
      key: "s2",
      question: "How do I manage notification preferences?",
      answer:
        "In Settings, navigate to 'Notifications' to customize which alerts you receive. You can enable or disable notifications for applications, messages, collaboration updates, and report availability. Choose between email, in-app, or both.",
    },
    {
      key: "s3",
      question: "Can I change my subscription plan?",
      answer:
        "Yes, you can upgrade or downgrade your subscription in the Settings under 'Billing'. Changes take effect immediately, and you'll be charged or credited prorated amounts based on your billing cycle.",
    },
    {
      key: "s4",
      question: "How do I manage team members and permissions?",
      answer:
        "In Settings, go to 'Users' to add or remove team members. You can assign different permission levels including admin, manager, and viewer roles. Each role has different access levels to campaigns, reports, and settings.",
    },
    {
      key: "s5",
      question: "How do I update payment methods?",
      answer:
        "Navigate to Settings > Billing > Payment Methods. You can add new credit cards, update existing payment methods, or set a default payment method. All payment information is securely encrypted and processed through our payment partner.",
    },
  ],
};

const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const { business } = useSelector((state: BusinessState) => state.business);
  const [activeTab, setActiveTab] = useState<string>("dashboard");

  useEffect(() => {
    localStorage.setItem(FAQ_ACCESSED_KEY, "true");
  }, []);

  const onClickSupport = async () => {
    const params = {
      context: {
        businessId: business?._id,
      },
    };
    await getSupportChat(params);
    navigate("/messenger");
  };

  const currentTab = faqTabs.find((tab) => tab.key === activeTab) || faqTabs[0];
  const currentQuestions = faqQuestions[activeTab] || [];

  return (
    <div className="faq">
      <div className="container">
        <div className="faq-content">
          <Row justify="center">
            <Col xs={24} style={{ display: "flex", justifyContent: "center" }}>
              <div className="faq-segmented-container">
                <Segmented
                  options={faqTabs.map((tab) => ({
                    label: tab.label,
                    value: tab.key,
                  }))}
                  value={activeTab}
                  onChange={(value) => setActiveTab(value as string)}
                />
              </div>
            </Col>
            <Col xs={24}>
              <div className="faq-tab-content">
                <div className="faq-video-container">
                  <div className="faq-video-wrapper">
                    <iframe
                      src={currentTab.videoUrl}
                      title={`Mustard - ${currentTab.label} Demo`}
                      frameBorder="0"
                      loading="lazy"
                      allowFullScreen
                      allow="clipboard-write"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        colorScheme: "light",
                      }}
                    ></iframe>
                  </div>
                  <div className="faq-video-description-container">
                    <p className="faq-video-description">
                      {currentTab.videoDescription}
                    </p>
                  </div>
                </div>

                <div className="faq-intro">
                  <h2 className="faq-section-title">Common Questions</h2>
                  <p className="faq-section-description">
                    Find answers to the most frequently asked questions about
                    using the {currentTab.label.toLowerCase()}.
                  </p>
                </div>

                <div className="faq-questions-container">
                  <Collapse
                    className="faq-collapse"
                    defaultActiveKey={[]}
                    expandIcon={({ isActive }) => (
                      <ExpandIcon isActive={!!isActive} />
                    )}
                    expandIconPosition="end"
                  >
                    {currentQuestions.map((item) => (
                      <Panel
                        header={
                          <span className="faq-question">{item.question}</span>
                        }
                        key={item.key}
                        className="faq-panel"
                      >
                        <p className="faq-answer">{item.answer}</p>
                      </Panel>
                    ))}
                  </Collapse>
                </div>
              </div>
            </Col>
          </Row>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div className="faq-box">
              <div className="faq-footer-content">
                <div>
                  <p className="faq-footer-title">Still need help?</p>
                  <p className="faq-footer-description">
                    Our support team is here for you.
                  </p>
                </div>
                <Button
                  type="primary"
                  size="middle"
                  icon={<FontAwesomeIcon icon={faHeadset} />}
                  onClick={onClickSupport}
                  className="faq-footer-button"
                >
                  Contact Support
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
